import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
const api = path => app.forum.attribute('apiUrl') + '/datahub/admin' + path;
const when = iso => iso ? new Date(iso).toLocaleDateString([], {
  dateStyle: 'medium'
}) : '—';
const emptyHolder = () => ({
  name: '',
  place: '',
  state: '',
  district: '',
  title: '',
  party: '',
  start: '',
  source: '',
  born: '',
  education: '',
  previousRoles: '',
  bio: ''
});

/** /data-hub/editor - review queue and office holders, for admins and Data editors. */
export default class DataEditorPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle('Data editor');
    this.tab = 'review';
    this.role = 'governor';
    this.review = null;
    this.holders = null;
    this.selected = {};
    this.form = emptyHolder();
    this.message = null;
    if (app.data.vouchaDataEditor) this.loadReview();
  }
  request(method, path, body) {
    return app.request({
      method,
      url: api(path),
      body,
      errorHandler: () => {}
    });
  }
  flash(type, text) {
    this.message = {
      type,
      text
    };
    m.redraw();
  }
  async loadReview() {
    this.review = null;
    try {
      this.review = (await this.request('GET', '/review')).items;
      this.selected = {};
    } catch (e) {
      this.flash('error', "Couldn't load the review queue.");
    }
    m.redraw();
  }
  async loadHolders() {
    this.holders = null;
    try {
      this.holders = await this.request('GET', '/officeholders?role=' + this.role);
    } catch (e) {
      this.flash('error', "Couldn't load office holders.");
    }
    m.redraw();
  }
  view() {
    if (!app.data.vouchaDataEditor) {
      return m("div", {
        className: "DataEditor container"
      }, m("p", null, "This page is for Data editors. Ask an admin if you'd like to help keep Voucha's data up to date."));
    }
    return m("div", {
      className: "DataEditor container"
    }, m("h1", null, m("i", {
      className: "fas fa-database"
    }), " Data editor"), m("p", {
      className: "DataEditor-intro"
    }, "Approve or reject collected changes, and keep governors, LGA chairpersons and other office holders current. Every name needs a source link."), m("nav", {
      className: "DataEditor-tabs"
    }, m(Button, {
      className: 'Button' + (this.tab === 'review' ? ' Button--primary' : ''),
      onclick: () => {
        this.tab = 'review';
        this.loadReview();
      }
    }, "Review queue"), m(Button, {
      className: 'Button' + (this.tab === 'holders' ? ' Button--primary' : ''),
      onclick: () => {
        this.tab = 'holders';
        this.loadHolders();
      }
    }, "Office holders"), m(Button, {
      className: 'Button' + (this.tab === 'projects' ? ' Button--primary' : ''),
      onclick: () => {
        this.tab = 'projects';
        this.loadProjects('pending');
      }
    }, "State projects"), m(Button, {
      className: 'Button' + (this.tab === 'prices' ? ' Button--primary' : ''),
      onclick: () => {
        this.tab = 'prices';
        this.loadPrices();
      }
    }, "Prices")), this.message ? m("p", {
      className: 'DataEditor-message is-' + this.message.type
    }, this.message.text) : null, this.tab === 'review' ? this.reviewView() : this.tab === 'projects' ? this.projectsView() : this.tab === 'prices' ? this.pricesView() : this.holdersView());
  }
  reviewView() {
    if (!this.review) return m(LoadingIndicator, null);
    if (!this.review.length) return m("p", {
      className: "DataEditor-muted"
    }, "Nothing waiting for review.");
    const ids = Object.keys(this.selected).filter(k => this.selected[k]).map(Number);
    return m("div", null, m("div", {
      className: "DataEditor-actions"
    }, m(Button, {
      className: "Button",
      onclick: () => this.selected = Object.fromEntries(this.review.map(r => [r.id, true]))
    }, "Select all"), m(Button, {
      className: "Button Button--primary",
      disabled: !ids.length,
      onclick: () => this.decide(ids, 'approve')
    }, "Approve ", ids.length || ''), m(Button, {
      className: "Button",
      disabled: !ids.length,
      onclick: () => this.decide(ids, 'reject')
    }, "Reject ", ids.length || '')), m("div", {
      className: "DataEditor-list"
    }, this.review.map(r => {
      const p = r.proposed;
      const c = r.current;
      const isPerson = r.target === 'officeholder';
      return m("label", {
        className: "DataEditor-item"
      }, m("input", {
        type: "checkbox",
        checked: !!this.selected[r.id],
        onchange: e => this.selected[r.id] = e.target.checked
      }), m("span", null, m("strong", null, p.place_name, p.district ? " · " + p.district : ""), m("span", {
        className: "DataEditor-muted"
      }, " \xB7 ", r.source), m("br", null), m("span", {
        className: "DataEditor-muted"
      }, "Now: ", c ? isPerson ? c.person_name : Number(c.value).toLocaleString() : 'nothing yet'), ' → ', m("strong", null, isPerson ? p.person_name + (p.party ? ' (' + p.party + ')' : '') : p.indicator + (p.period ? ' (' + p.period + ')' : '') + ': ' + Number(p.value).toLocaleString()), p.source_url ? m("a", {
        href: p.source_url,
        target: "_blank",
        rel: "noopener"
      }, ' ', "source \u2197") : null));
    })));
  }
  async decide(ids, decision) {
    try {
      const r = await this.request('POST', '/review', {
        ids,
        decision
      });
      this.flash('success', r.decided + (decision === 'approve' ? ' approved and live.' : ' rejected.'));
    } catch (e) {
      this.flash('error', "Couldn't save.");
    }
    this.loadReview();
  }
  holdersView() {
    const h = this.holders;
    if (!h) return m(LoadingIndicator, null);
    const f = this.form;
    const lgaRole = ['lga_chairman', 'lga_vice_chairman', 'ward_councillor'].includes(this.role);
    const placeLabel = lgaRole ? 'LGA' : this.role === 'mayor' ? 'City' : 'State';
    const seatLabel = this.role === 'ward_councillor' ? 'Ward' : this.role === 'assembly_member' ? 'Constituency' : null;
    return m("div", null, m("nav", {
      className: "DataEditor-tabs is-small"
    }, Object.entries(h.roles).map(([k, v]) => m(Button, {
      className: 'Button' + (this.role === k ? ' Button--primary' : ''),
      onclick: () => {
        this.role = k;
        this.loadHolders();
      }
    }, v))), h.missing && h.missing.length ? m("p", {
      className: "DataEditor-message is-warn"
    }, "Still missing (", h.missing.length, "): ", h.missing.join(', ')) : null, m("div", {
      className: "DataEditor-list"
    }, h.current.length ? h.current.map(x => m("div", {
      className: "DataEditor-item"
    }, m("span", null, m("strong", null, x.place), ": ", x.name, x.party ? ' (' + x.party + ')' : '', m("span", {
      className: "DataEditor-muted"
    }, x.start ? ' · since ' + x.start : '', " \xB7 checked ", when(x.checked)), x.source ? m("a", {
      href: x.source,
      target: "_blank",
      rel: "noopener"
    }, ' ', "\u2197") : null))) : m("p", {
      className: "DataEditor-muted"
    }, "None recorded yet.")), m("div", {
      className: "DataEditor-form"
    }, m("h3", null, "Add or replace"), m("p", {
      className: "DataEditor-muted"
    }, "The previous holder for that ", seatLabel ? seatLabel.toLowerCase() : 'place', " is kept as history. For many names at once, upload a CSV on the matching Data Hub source (Admin \u203A Data Hub \u203A Sources)."), m("div", {
      className: "DataEditor-row"
    }, m("input", {
      className: "FormControl",
      placeholder: placeLabel,
      value: f.place,
      oninput: e => f.place = e.target.value
    }), lgaRole ? m("input", {
      className: "FormControl",
      placeholder: "State",
      value: f.state,
      oninput: e => f.state = e.target.value
    }) : null, seatLabel ? m("input", {
      className: "FormControl",
      placeholder: seatLabel,
      value: f.district || '',
      oninput: e => f.district = e.target.value
    }) : null, this.role === 'assembly_member' ? m("input", {
      className: "FormControl",
      placeholder: "Title (Speaker, Deputy Speaker\u2026), optional",
      value: f.title || '',
      oninput: e => f.title = e.target.value
    }) : null, m("input", {
      className: "FormControl",
      placeholder: "Name",
      value: f.name,
      oninput: e => f.name = e.target.value
    })), m("div", {
      className: "DataEditor-row"
    }, m("input", {
      className: "FormControl",
      placeholder: "Party",
      value: f.party,
      oninput: e => f.party = e.target.value
    }), m("input", {
      className: "FormControl",
      type: "date",
      title: "In office since",
      value: f.start,
      oninput: e => f.start = e.target.value
    }), m("input", {
      className: "FormControl",
      placeholder: "Source link (https://...)",
      value: f.source,
      oninput: e => f.source = e.target.value
    })), m("div", {
      className: "DataEditor-row"
    }, m("input", {
      className: "FormControl",
      type: "date",
      title: "Date of birth",
      value: f.born,
      oninput: e => f.born = e.target.value
    }), m("input", {
      className: "FormControl",
      placeholder: "Education (e.g. BSc Economics, University of Lagos)",
      value: f.education,
      oninput: e => f.education = e.target.value
    }), m("input", {
      className: "FormControl",
      placeholder: "Previous roles",
      value: f.previousRoles,
      oninput: e => f.previousRoles = e.target.value
    })), m("textarea", {
      className: "FormControl DataEditor-bio",
      rows: "3",
      placeholder: "Short bio (two or three sentences)",
      value: f.bio,
      oninput: e => f.bio = e.target.value
    }), m(Button, {
      className: "Button Button--primary",
      onclick: () => this.saveHolder()
    }, "Save")));
  }
  async saveHolder() {
    if (!this.form.source) {
      this.flash('error', 'Add a source link so others can check it.');
      return;
    }
    try {
      await this.request('POST', '/officeholders', {
        role: this.role,
        ...this.form
      });
      this.form = emptyHolder();
      this.flash('success', 'Saved.');
      this.loadHolders();
    } catch (e) {
      this.flash('error', e?.response?.message || "Couldn't save.");
    }
  }

  // ---------------- reference prices (materials, labour and services by state, for projects) ----------------
  async loadPrices() {
    this.prices = null;
    this.priceCheck = null;
    this.priceQ = this.priceQ || '';
    try {
      this.prices = await this.request('GET', '/prices?q=' + encodeURIComponent(this.priceQ));
    } catch (e) {
      this.flash('error', e.response && e.response.error || "Couldn't load prices.");
    }
    m.redraw();
  }
  async blankPrices() {
    try {
      const r = await this.request('GET', '/prices?blank=1');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob(['﻿' + r.csv], {
        type: 'text/csv;charset=utf-8'
      }));
      a.download = 'voucha-reference-prices.csv';
      a.click();
    } catch (e) {
      this.flash('error', "Couldn't make the file.");
    }
  }
  async readPriceFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    this.priceCsv = await file.text();
    this.checkPrices(false);
  }
  async checkPrices(save) {
    if (!this.priceCsv) return;
    this.priceBusy = true;
    try {
      const r = await this.request('POST', '/prices', {
        csv: this.priceCsv,
        save
      });
      if (r.saved) {
        this.flash('success', 'Saved: ' + r.added + ' new, ' + r.updated + ' updated.');
        this.priceCsv = null;
        this.priceCheck = null;
        this.prices = {
          total: r.total,
          prices: r.prices
        };
      } else {
        this.priceCheck = r;
      }
    } catch (e) {
      this.flash('error', e.response && e.response.error || "Couldn't check the file.");
    }
    this.priceBusy = false;
    m.redraw();
  }
  async deletePrice(p) {
    if (!confirm('Remove ' + p.item + ' (' + p.state + ', ' + p.date + ')?')) return;
    await this.request('DELETE', '/prices/' + p.id).catch(() => {});
    this.loadPrices();
  }
  pricesView() {
    const ch = this.priceCheck;
    const money = (v, c) => (c === 'NGN' ? '₦' : c + ' ') + Number(v).toLocaleString('en-US', {
      maximumFractionDigits: 2
    });
    return m("div", {
      className: "DataEditor-prices"
    }, m("p", {
      className: "DataEditor-intro"
    }, "Typical prices of materials, equipment, labour and services by state. Projects in Nigeria show the newest price for their state (or the national one) as a reference price, never as a quote. Upload a CSV with the columns item_key, item, category, unit, state (or National), locality, price, currency, source, source_url and date (YYYY-MM-DD). The same item, unit, place and date replaces the old price."), m("div", {
      className: "DataEditor-actions"
    }, m(Button, {
      className: "Button",
      icon: "fas fa-file-csv",
      onclick: () => this.blankPrices()
    }, "Blank CSV with the template items"), m("label", {
      className: "Button"
    }, m("i", {
      className: "fas fa-upload"
    }), " Choose a CSV", m("input", {
      type: "file",
      accept: ".csv,text/csv",
      style: {
        display: 'none'
      },
      onchange: e => this.readPriceFile(e)
    }))), ch ? m("div", {
      className: 'DataEditor-message is-' + (ch.errors.length ? 'warn' : 'success')
    }, m("strong", null, ch.ok, " of ", ch.total, " rows are fine."), ch.errors.length ? m("ul", null, ch.errors.slice(0, 30).map(e => m("li", null, "Line ", e.line, e.item ? ' (' + e.item + ')' : '', ": ", e.problems.join(', '))), ch.errors.length > 30 ? m("li", null, "\u2026and ", ch.errors.length - 30, " more") : null) : null, ch.errors.length ? m("p", null, "Fix these rows and choose the file again: nothing is saved until every row is fine.") : m(Button, {
      className: "Button Button--primary",
      loading: this.priceBusy,
      onclick: () => this.checkPrices(true)
    }, "Save ", ch.ok, " prices")) : null, m("div", {
      className: "DataEditor-row"
    }, m("input", {
      className: "FormControl",
      placeholder: "Find an item",
      value: this.priceQ,
      oninput: e => this.priceQ = e.target.value,
      onkeydown: e => e.key === 'Enter' && this.loadPrices()
    }), m(Button, {
      className: "Button",
      onclick: () => this.loadPrices()
    }, "Search")), this.prices === null ? m(LoadingIndicator, null) : !this.prices.prices.length ? m("p", {
      className: "DataEditor-muted"
    }, "No prices yet.") : m("div", {
      className: "DataEditor-list"
    }, m("p", {
      className: "DataEditor-muted"
    }, this.prices.total, " prices in all; the newest ", this.prices.prices.length, " shown."), this.prices.prices.map(p => m("div", {
      className: "DataEditor-item"
    }, m("strong", null, p.item), " ", m("span", {
      className: "DataEditor-muted"
    }, p.key, " \xB7 ", p.category), m("div", null, money(p.price, p.currency), " per ", p.unit, " \xB7 ", p.locality ? p.locality + ', ' : '', p.state, " \xB7 ", p.date), m("div", {
      className: "DataEditor-muted"
    }, p.sourceUrl ? m("a", {
      href: p.sourceUrl,
      target: "_blank",
      rel: "noopener nofollow"
    }, p.source) : p.source, ' · ', m("a", {
      href: "#",
      onclick: e => {
        e.preventDefault();
        this.deletePrice(p);
      }
    }, "Remove"))))));
  }

  // ---------------- state projects (members' submissions) ----------------
  async loadProjects(review) {
    this.projReview = review;
    this.projects = null;
    this.projEdit = null;
    try {
      const r = await this.request('GET', '/projects?review=' + review);
      this.projects = r.items;
      this.projMeta = {
        sectors: r.sectors,
        statuses: r.statuses
      };
    } catch (e) {
      this.flash('error', "Couldn't load projects.");
    }
    m.redraw();
  }
  async decideProject(p, action, edits) {
    if (action === 'delete' && !confirm('Delete "' + p.title + '" for good?')) return;
    try {
      await this.request('POST', '/projects/' + p.id + '/' + action, edits ? {
        edits
      } : {});
      this.flash('success', {
        approve: 'Approved: it shows on the state page.',
        reject: 'Rejected.',
        save: 'Saved.',
        delete: 'Deleted.'
      }[action]);
      this.loadProjects(this.projReview);
    } catch (e) {
      this.flash('error', e.response && e.response.error || "Couldn't save.");
    }
  }
  projectsView() {
    const meta = this.projMeta || {
      sectors: {},
      statuses: {}
    };
    const tabs = [['pending', 'Waiting for review'], ['approved', 'Live'], ['rejected', 'Rejected']];
    const money = p => p.amount ? (p.currency === 'USD' ? '$' : '₦') + p.amount.toLocaleString('en-US') + 'm' : null;
    return m("div", {
      className: "DataEditor-projects"
    }, m("p", {
      className: "DataEditor-intro"
    }, "Investments and big projects members sent in for the state pages. Check the source link before approving; you can correct the details first."), m("nav", {
      className: "DataEditor-tabs"
    }, tabs.map(([k, label]) => m(Button, {
      className: 'Button Button--text' + (this.projReview === k ? ' active' : ''),
      onclick: () => this.loadProjects(k)
    }, label))), this.projects === null ? m(LoadingIndicator, null) : !this.projects.length ? m("p", {
      className: "DataEditor-muted"
    }, "Nothing here.") : m("div", {
      className: "DataEditor-list"
    }, this.projects.map(p => this.projEdit && this.projEdit.id === p.id ? this.projectEditor(p, meta) : m("div", {
      className: "DataEditor-item"
    }, m("strong", null, p.title), " ", m("span", {
      className: "DataEditor-muted"
    }, p.state, p.lga ? ', ' + p.lga : ''), m("div", {
      className: "DataEditor-muted"
    }, [meta.statuses[p.status], meta.sectors[p.sector], p.backer, money(p), p.expected ? 'due ' + p.expected : null].filter(Boolean).join(' · ')), p.summary ? m("p", null, p.summary) : null, m("div", {
      className: "DataEditor-muted"
    }, p.source ? m("a", {
      href: p.source,
      target: "_blank",
      rel: "noopener nofollow"
    }, "Source") : 'No source', p.by ? ' · sent by @' + p.by : '', ' · ' + when(p.createdAt)), m("div", {
      className: "DataEditor-actions"
    }, p.review !== 'approved' ? m(Button, {
      className: "Button Button--primary",
      onclick: () => this.decideProject(p, 'approve')
    }, "Approve") : null, m(Button, {
      className: "Button",
      onclick: () => this.projEdit = {
        id: p.id,
        f: {
          title: p.title,
          sector: p.sector || '',
          status: p.status,
          backer: p.backer || '',
          amount: p.amount || '',
          currency: p.currency || 'NGN',
          announcedOn: p.announcedOn || '',
          expected: p.expected || '',
          summary: p.summary || '',
          source: p.source || '',
          lgaId: p.lgaId || ''
        }
      }
    }, "Edit"), p.review !== 'rejected' ? m(Button, {
      className: "Button",
      onclick: () => this.decideProject(p, 'reject')
    }, "Reject") : null, m(Button, {
      className: "Button Button--danger",
      onclick: () => this.decideProject(p, 'delete')
    }, "Delete"))))));
  }
  projectEditor(p, meta) {
    const f = this.projEdit.f;
    const bind = k => ({
      value: f[k],
      oninput: e => f[k] = e.target.value
    });
    const opts = o => Object.entries(o).map(([k, v]) => m("option", {
      value: k
    }, v));
    return m("div", {
      className: "DataEditor-item DataEditor-form"
    }, m("input", Object.assign({
      className: "FormControl",
      placeholder: "Project name"
    }, bind('title'))), m("div", {
      className: "DataEditor-row"
    }, m("select", Object.assign({
      className: "FormControl"
    }, bind('sector')), m("option", {
      value: ""
    }, "Sector\u2026"), opts(meta.sectors)), m("select", Object.assign({
      className: "FormControl"
    }, bind('status')), opts(meta.statuses))), m("div", {
      className: "DataEditor-row"
    }, m("input", Object.assign({
      className: "FormControl",
      placeholder: "Who is behind it"
    }, bind('backer'))), m("input", Object.assign({
      className: "FormControl",
      placeholder: "Amount (millions)"
    }, bind('amount'))), m("select", Object.assign({
      className: "FormControl"
    }, bind('currency')), m("option", {
      value: "NGN"
    }, "NGN"), m("option", {
      value: "USD"
    }, "USD"))), m("div", {
      className: "DataEditor-row"
    }, m("input", Object.assign({
      className: "FormControl",
      type: "date"
    }, bind('announcedOn'))), m("input", Object.assign({
      className: "FormControl",
      placeholder: "Expected completion"
    }, bind('expected')))), m("textarea", Object.assign({
      className: "FormControl",
      rows: "3",
      placeholder: "What it is"
    }, bind('summary'))), m("input", Object.assign({
      className: "FormControl",
      placeholder: "Source link"
    }, bind('source'))), m("div", {
      className: "DataEditor-actions"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: () => this.decideProject(p, p.review === 'approved' ? 'save' : 'approve', {
        ...f
      })
    }, p.review === 'approved' ? 'Save' : 'Save and approve'), p.review !== 'approved' ? m(Button, {
      className: "Button",
      onclick: () => this.decideProject(p, 'save', {
        ...f
      })
    }, "Save only") : null, m(Button, {
      className: "Button",
      onclick: () => this.projEdit = null
    }, "Cancel")));
  }
}